import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { UploadCloud, FileText, Image, Loader2, Download, RefreshCw, Sparkles, ClipboardPaste, Trash2, Mail, CheckCircle2, AlertTriangle } from "lucide-react";
import CircularProgress from "../components/CircularProgress";
import UpgradeModal from "../components/UpgradeModal";
import SpeechPlayer from "../components/SpeechPlayer";
import { useSpeech, getSpeechPrefs, saveSpeechPrefs } from "../hooks/useSpeech";
import { toast } from "sonner";

const PLAN_LABEL = { free: "FREE", pro: "PRO", business: "BUSINESS" };
const PLAN_COLOR = { free: "bg-zinc-500/20 text-zinc-300 border-zinc-500/40", pro: "bg-indigo-500/20 text-indigo-300 border-indigo-500/40", business: "bg-purple-500/20 text-purple-300 border-purple-500/40" };
const PLAN_MAX   = { free: 500, pro: 10000, business: 50000 };

const READING_STYLE = {
  backgroundColor: "rgba(99,102,241,0.28)",
  borderRadius: "3px",
  boxShadow: "0 0 0 2px rgba(99,102,241,0.22)",
  paddingInline: "2px",
  transition: "background-color 0.15s ease",
};

export default function Dashboard() {
  const { user, refresh, emailVerified, resendVerification } = useAuth();
  const [verifyResent, setVerifyResent] = useState(false);
  const [batchFiles, setBatchFiles]     = useState([]);
  const [batchResults, setBatchResults] = useState([]);
  const [batchRunning, setBatchRunning] = useState(false);
  const batchRef = useRef();
  const [doc, setDoc]         = useState(null);
  const [loading, setLoading] = useState(false);
  const [paraphrasing, setParaphrasing] = useState(false);
  const [upgrade, setUpgrade] = useState({ open: false, reason: "" });
  const [step, setStep]       = useState("");
  const [mode, setMode]       = useState("upload");
  const [pasted, setPasted]   = useState("");
  const fileRef = useRef();

  // Speech
  const { supported, browserSupported, voices, speaking, paused, currentSentIdx, progress, speak, stop, pause, resume } = useSpeech();
  const prefs0 = getSpeechPrefs();
  const [speechRate, setSpeechRate]   = useState(prefs0.rate    || 1);
  const [speechVoice, setSpeechVoice] = useState(prefs0.voiceName || "");
  const [engineMode, setEngineMode]   = useState(prefs0.engineMode || "browser");
  const sentRefs = useRef({});

  // Auto-scroll to currently spoken sentence
  useEffect(() => {
    if (currentSentIdx >= 0 && sentRefs.current[currentSentIdx]) {
      sentRefs.current[currentSentIdx].scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [currentSentIdx]);

  // Stop speech on unmount
  useEffect(() => () => stop(), []); // eslint-disable-line

  const plan       = user?.plan || "free";
  const maxWords   = PLAN_MAX[plan];
  const pastedWordCount = pasted.trim() ? pasted.trim().split(/\s+/).length : 0;

  const handleResult = (data) => {
    setDoc(data);
    stop();
    toast.success("Scan complete");
    refresh();
  };

  const handleErr = (err, fallbackMsg) => {
    const detail = err?.response?.data?.detail || fallbackMsg;
    if (detail.toLowerCase().includes("limit") || detail.toLowerCase().includes("plan") || detail.toLowerCase().includes("upgrade")) {
      setUpgrade({ open: true, reason: detail });
    } else {
      toast.error(detail);
    }
  };

  const upload = async (file) => {
    if (!file) return;
    setLoading(true); setStep("Uploading & parsing document…"); setDoc(null); stop();
    try {
      const fd = new FormData();
      fd.append("file", file);
      setStep("Scanning for AI content…");
      const { data } = await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      handleResult(data);
    } catch (err) { handleErr(err, "Upload failed"); }
    finally { setLoading(false); setStep(""); }
  };

  const submitPasted = async () => {
    if (!pasted.trim()) { toast.error("Paste some text first"); return; }
    setLoading(true); setStep("Scanning pasted text for AI content…"); setDoc(null); stop();
    try {
      const { data } = await api.post("/documents/paste", { text: pasted, title: "Pasted Document" });
      handleResult(data);
    } catch (err) { handleErr(err, "Failed to scan"); }
    finally { setLoading(false); setStep(""); }
  };

  const onPick = (e) => upload(e.target.files?.[0]);
  const onDrop = (e) => { e.preventDefault(); upload(e.dataTransfer.files?.[0]); };

  const MAX_BATCH = 10;
  const onBatchPick = (e) => {
    const picked = Array.from(e.target.files || []).slice(0, MAX_BATCH);
    setBatchFiles(picked);
    setBatchResults([]);
  };

  const runBatch = async () => {
    if (!batchFiles.length) return;
    setBatchRunning(true);
    setBatchResults([]);
    const results = [];
    for (let i = 0; i < batchFiles.length; i++) {
      const file = batchFiles[i];
      results.push({ name: file.name, status: "processing", aiPct: null });
      setBatchResults([...results]);
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        results[i] = { name: file.name, status: "done", aiPct: data.finalAiPercentage ?? 0, id: data.id };
      } catch (err) {
        const msg = err?.response?.data?.detail || "Failed";
        results[i] = { name: file.name, status: "error", msg };
      }
      setBatchResults([...results]);
    }
    setBatchRunning(false);
    refresh();
    toast.success(`Batch complete — ${results.filter(r => r.status === "done").length}/${results.length} succeeded`);
  };

  const paraphrase = async () => {
    setParaphrasing(true); setStep("Paraphrasing flagged sentences & re-scanning…"); stop();
    try {
      const { data } = await api.post(`/documents/${doc.id}/paraphrase`);
      setDoc(data);
      toast.success(`Round ${data.rounds.length} complete: ${data.finalAiPercentage}% AI`);
    } catch (err) { handleErr(err, "Paraphrase failed"); }
    finally { setParaphrasing(false); setStep(""); }
  };

  const download = async () => {
    const { data } = await api.get(`/documents/${doc.id}/download`);
    let blob;
    if (data.encoding === "base64") {
      const bytes = atob(data.content);
      const arr = new Uint8Array(bytes.length);
      for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
      blob = new Blob([arr], { type: data.mimeType });
    } else {
      blob = new Blob([data.content], { type: "text/plain" });
    }
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = data.fileName; a.click();
    URL.revokeObjectURL(url);
  };

  const lastScan    = doc?.lastScan || {};
  const scores      = lastScan.sentence_scores || [];
  const aiPct       = doc?.finalAiPercentage ?? 0;
  const plagPct     = doc?.finalPlagiarismPercentage ?? 0;
  const humanPct    = Math.max(0, 100 - aiPct);
  const flaggedCount = scores.filter(s => s.isAI).length;
  const overLimit   = pastedWordCount > maxWords;

  // Speech handlers
  const handlePlay = () => {
    if (paused) { resume(); return; }
    speak(scores.map(s => s.text), speechRate, speechVoice);
  };
  const handleRateChange = (r) => { setSpeechRate(r); saveSpeechPrefs({ rate: r }); };
  const handleVoiceChange = (v) => { setSpeechVoice(v); saveSpeechPrefs({ voiceName: v }); };
  const handleEngineMode  = (m) => { setEngineMode(m); saveSpeechPrefs({ engineMode: m }); };

  const handleResendVerify = async () => {
    try { await resendVerification(); setVerifyResent(true); toast.success("Verification email sent — check your inbox."); }
    catch { toast.error("Could not send verification email."); }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-12 py-10 lg:py-14">
      {!emailVerified && (
        <div className="mb-6 flex items-center gap-3 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-300">
          <AlertTriangle size={16} className="flex-shrink-0"/>
          <span className="flex-1">Please verify your email address to unlock all features.</span>
          {verifyResent
            ? <span className="flex items-center gap-1 text-emerald-400"><CheckCircle2 size={14}/> Sent</span>
            : <button onClick={handleResendVerify} className="flex items-center gap-1 underline underline-offset-2 hover:text-amber-200"><Mail size={13}/>Resend</button>
          }
        </div>
      )}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-zinc-500">Dashboard</div>
          <h1 className="mt-1 text-3xl sm:text-4xl font-bold tracking-tight" style={{fontFamily:"'Cabinet Grotesk', sans-serif"}}>Humanize a document</h1>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-xs px-2.5 py-1 rounded-md border ${PLAN_COLOR[plan]}`}>{PLAN_LABEL[plan]} PLAN</span>
        </div>
      </div>

      {!doc && (
        <>
          <div className="mt-8 inline-flex flex-wrap p-1 rounded-xl glass">
            <button onClick={() => setMode("upload")} data-testid="mode-upload"
              className={`px-4 sm:px-5 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors flex items-center gap-2 ${mode === "upload" ? "bg-indigo-600 text-white" : "text-zinc-400 hover:text-white"}`}>
              <UploadCloud size={14}/> Upload File
            </button>
            <button onClick={() => setMode("paste")} data-testid="mode-paste"
              className={`px-4 sm:px-5 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors flex items-center gap-2 ${mode === "paste" ? "bg-indigo-600 text-white" : "text-zinc-400 hover:text-white"}`}>
              <ClipboardPaste size={14}/> Paste Text
            </button>
            {plan === "business" && (
              <button onClick={() => setMode("batch")} data-testid="mode-batch"
                className={`px-4 sm:px-5 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors flex items-center gap-2 ${mode === "batch" ? "bg-purple-600 text-white" : "text-zinc-400 hover:text-white"}`}>
                <FileText size={14}/> Batch <span className="text-[9px] ml-0.5 px-1 py-0.5 rounded bg-purple-600/40 text-purple-300">BUSINESS</span>
              </button>
            )}
          </div>

          {mode === "upload" && (
            <div onDragOver={e=>e.preventDefault()} onDrop={onDrop} onClick={()=>fileRef.current?.click()}
              data-testid="upload-zone"
              className="mt-5 cursor-pointer rounded-3xl border-2 border-dashed border-white/[0.12] hover:border-indigo-500/50 hover:bg-indigo-500/5 transition-all p-12 text-center glass">
              <input ref={fileRef} type="file" accept=".pdf,.docx,.txt,.pptx,.jpg,.jpeg,.png,.webp" onChange={onPick} hidden data-testid="file-input"/>
              <div className="flex items-center justify-center gap-3">
                <UploadCloud size={36} className="text-indigo-300"/>
                <Image size={28} className="text-purple-300"/>
              </div>
              <div className="mt-4 text-lg font-semibold">Drop your file here</div>
              <div className="mt-1 text-sm text-zinc-400">Documents & images supported</div>
              <div className="mt-4 inline-flex flex-wrap gap-2 justify-center">
                {["PDF","DOCX","TXT","PPTX","JPG","PNG","WEBP"].map(t => (
                  <span key={t} className="text-[10px] tracking-widest uppercase px-2 py-1 rounded glass">{t}</span>
                ))}
              </div>
              {loading && (
                <div className="mt-6 flex items-center justify-center gap-2 text-indigo-300 text-sm">
                  <Loader2 size={14} className="animate-spin"/>{step || "Processing…"}
                </div>
              )}
            </div>
          )}

          {mode === "paste" && (
            <div className="mt-5 glass-strong rounded-3xl p-5 sm:p-6">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="text-sm font-semibold">Paste your text</div>
                <div className="flex items-center gap-2 text-xs text-zinc-400">
                  <span className={overLimit ? "text-red-400 font-bold" : "text-zinc-300 font-medium"}>{pastedWordCount.toLocaleString()}</span>
                  <span>/ {maxWords.toLocaleString()} words</span>
                  {overLimit && <span className="text-red-400">· over limit</span>}
                  {pasted.length > 0 && (
                    <button onClick={() => setPasted("")} className="ml-2 p-1 rounded hover:bg-white/10" title="Clear" data-testid="paste-clear-btn">
                      <Trash2 size={12}/>
                    </button>
                  )}
                </div>
              </div>
              <Textarea data-testid="paste-textarea" value={pasted} onChange={e=>setPasted(e.target.value)}
                placeholder="Paste a long article, report, essay, thesis, or any AI-generated text here."
                className="mt-3 min-h-[280px] sm:min-h-[360px] max-h-[60vh] font-mono text-sm leading-relaxed resize-y bg-white/[0.02] border-white/[0.08] focus-visible:ring-indigo-500"/>
              <div className="mt-3 flex items-center justify-between flex-wrap gap-3 text-xs text-zinc-500">
                <span>Tip: works with text up to <b className="text-zinc-300">{maxWords.toLocaleString()}</b> words on your {PLAN_LABEL[plan]} plan.</span>
                <Button onClick={submitPasted} disabled={loading || !pasted.trim() || overLimit}
                  className="bg-indigo-600 hover:bg-indigo-700 glow-primary" data-testid="paste-submit-btn">
                  {loading ? <><Loader2 size={14} className="animate-spin mr-2"/>Scanning…</> : <><Sparkles size={14} className="mr-2"/>Scan & Humanize</>}
                </Button>
              </div>
              {loading && (
                <div className="mt-4 flex items-center gap-2 text-indigo-300 text-sm">
                  <Loader2 size={14} className="animate-spin"/>{step}
                </div>
              )}
            </div>
          )}

          {mode === "batch" && plan === "business" && (
            <div className="mt-5 glass-strong rounded-3xl p-5 sm:p-6">
              <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                <div>
                  <div className="text-sm font-semibold">Batch upload</div>
                  <div className="text-xs text-zinc-400 mt-0.5">Upload up to {MAX_BATCH} documents at once — each is scanned and humanized independently.</div>
                </div>
                <Button onClick={() => batchRef.current?.click()} variant="outline" className="glass text-xs" disabled={batchRunning}>
                  <UploadCloud size={13} className="mr-1.5"/>Choose files
                </Button>
                <input ref={batchRef} type="file" multiple accept=".pdf,.docx,.txt,.pptx" onChange={onBatchPick} hidden/>
              </div>

              {batchFiles.length > 0 && (
                <>
                  <div className="space-y-2 mb-4">
                    {batchFiles.map((f, i) => {
                      const r = batchResults[i];
                      return (
                        <div key={i} className="flex items-center gap-3 rounded-lg glass px-3 py-2 text-sm">
                          <FileText size={14} className="text-zinc-400 flex-shrink-0"/>
                          <span className="flex-1 truncate text-xs">{f.name}</span>
                          {!r && <span className="text-[10px] text-zinc-500">Queued</span>}
                          {r?.status === "processing" && <Loader2 size={13} className="animate-spin text-indigo-400"/>}
                          {r?.status === "done" && (
                            <span className={`text-[10px] font-semibold ${r.aiPct === 0 ? "text-emerald-400" : "text-amber-400"}`}>
                              {r.aiPct}% AI
                            </span>
                          )}
                          {r?.status === "error" && <span className="text-[10px] text-red-400 truncate max-w-[120px]">{r.msg}</span>}
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <span className="text-xs text-zinc-500">{batchFiles.length} file{batchFiles.length !== 1 ? "s" : ""} selected</span>
                    <div className="flex gap-2">
                      <Button onClick={() => { setBatchFiles([]); setBatchResults([]); batchRef.current.value = ""; }} variant="ghost" className="text-zinc-400 text-xs" disabled={batchRunning}>
                        Clear
                      </Button>
                      <Button onClick={runBatch} disabled={batchRunning || batchFiles.length === 0} className="bg-purple-600 hover:bg-purple-700 text-xs">
                        {batchRunning ? <><Loader2 size={13} className="animate-spin mr-1.5"/>Processing…</> : <><Sparkles size={13} className="mr-1.5"/>Run Batch</>}
                      </Button>
                    </div>
                  </div>
                </>
              )}

              {batchFiles.length === 0 && (
                <div className="rounded-xl border-2 border-dashed border-white/10 p-8 text-center text-zinc-500 text-sm">
                  No files selected yet. Click "Choose files" to pick up to {MAX_BATCH} documents.
                </div>
              )}
            </div>
          )}
        </>
      )}

      {doc && (
        <div className="mt-8 grid lg:grid-cols-3 gap-6">
          {/* Left panel: stats + actions */}
          <div className="lg:col-span-1 glass-strong rounded-3xl p-6 animate-fade-up">
            <div className="flex items-center gap-2 text-zinc-400 text-xs uppercase tracking-widest">
              <FileText size={14}/>{doc.fileType?.toUpperCase()}
            </div>
            <div className="mt-1 font-semibold truncate" title={doc.fileName}>{doc.fileName}</div>
            <div className="text-xs text-zinc-500 mt-0.5">{doc.originalWordCount.toLocaleString()} words</div>

            <div className="mt-6 flex justify-center">
              <CircularProgress value={aiPct} color={aiPct > 0 ? "#EF4444" : "#10B981"} label="AI Detected" sublabel={`${humanPct}% Human`}/>
            </div>

            <div className="mt-6 grid grid-cols-2 gap-2">
              <div className="glass rounded-lg p-3">
                <div className="text-[10px] uppercase tracking-widest text-zinc-400">Plagiarism</div>
                <div className="text-xl font-bold mt-0.5">{plagPct}%</div>
              </div>
              <div className="glass rounded-lg p-3">
                <div className="text-[10px] uppercase tracking-widest text-zinc-400">Flagged</div>
                <div className="text-xl font-bold mt-0.5">{flaggedCount}</div>
              </div>
            </div>

            <div className="mt-6 space-y-2 text-xs">
              <div className="text-zinc-400 uppercase tracking-widest">Rounds</div>
              <div className="flex flex-wrap gap-1.5">
                {(doc.rounds || []).map(r => (
                  <span key={r.roundNumber} className="px-2 py-1 rounded glass text-[11px]">R{r.roundNumber}: {r.aiPercentageBefore}% → {r.aiPercentageAfter}%</span>
                ))}
                {(doc.rounds || []).length === 0 && <span className="text-zinc-500">Initial scan only</span>}
              </div>
            </div>

            <div className="mt-6 flex flex-col gap-2">
              {aiPct > 0 ? (
                <Button onClick={paraphrase} disabled={paraphrasing} className="bg-indigo-600 hover:bg-indigo-700 glow-primary" data-testid="paraphrase-btn">
                  {paraphrasing ? <><Loader2 size={14} className="animate-spin mr-2"/>Paraphrasing…</> : <><Sparkles size={14} className="mr-2"/>Paraphrase Round {(doc.rounds?.length||0)+1}</>}
                </Button>
              ) : (
                <div className="rounded-xl p-3 bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-sm flex items-center gap-2">
                  <Sparkles size={14}/> 100% Human-written ✨
                </div>
              )}
              <Button onClick={download} variant="outline" className="glass" data-testid="download-btn">
                <Download size={14} className="mr-2"/>Download humanized
              </Button>
              <Button onClick={() => { setDoc(null); setPasted(""); stop(); }} variant="ghost" className="text-zinc-400" data-testid="new-doc-btn">
                <RefreshCw size={14} className="mr-2"/>New document
              </Button>
            </div>
          </div>

          {/* Right panel: sentence breakdown + speech player */}
          <div className="lg:col-span-2 glass-strong rounded-3xl p-6 animate-fade-up flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold">Sentence breakdown</h3>
              <div className="flex items-center gap-3 text-xs">
                <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-red-500/50"/>AI</span>
                <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-emerald-500/50"/>Human</span>
                {speaking && <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-indigo-500/50"/>Reading</span>}
              </div>
            </div>

            <div className="prose prose-invert max-w-none text-sm leading-7 flex-1 max-h-[55vh] overflow-y-auto pr-2 scroll-slim">
              {scores.length === 0 && <div className="text-zinc-500 text-sm">No analysis yet.</div>}
              {scores.map(s => (
                <span
                  key={s.idx}
                  ref={el => { sentRefs.current[s.idx] = el; }}
                  className={s.isAI ? "sentence-ai" : "sentence-human"}
                  style={currentSentIdx === s.idx ? READING_STYLE : {}}
                  title={`AI score: ${Math.round(s.score)}%`}
                >
                  {s.text}{" "}
                </span>
              ))}
            </div>

            {scores.length > 0 && (
              <SpeechPlayer
                supported={supported}
                browserSupported={browserSupported}
                speaking={speaking}
                paused={paused}
                progress={progress}
                voices={voices}
                rate={speechRate}
                voiceName={speechVoice}
                engineMode={engineMode}
                onPlay={handlePlay}
                onPause={pause}
                onStop={stop}
                onRateChange={handleRateChange}
                onVoiceChange={handleVoiceChange}
                onEngineModeChange={handleEngineMode}
              />
            )}
          </div>
        </div>
      )}

      <UpgradeModal open={upgrade.open} onOpenChange={(o)=>setUpgrade({open:o,reason:upgrade.reason})} reason={upgrade.reason}/>
    </div>
  );
}
